import React, { useState, useEffect, useMemo, useRef } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import { toast } from 'react-toastify'
import { callZapier } from 'utils/zapier/callZapier'
import { NumericFormat } from 'react-number-format'
import html2canvas from 'html2canvas'

import {
  Card,
  Grid,
  Typography,
  Collapse,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  InputAdornment,
  createFilterOptions,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material'
import { Check, ExpandMore } from '@mui/icons-material'
import FormField from 'components/FormField'

import {
  format,
  differenceInYears,
  differenceInMonths,
  addMonths,
  subYears,
} from 'date-fns'
import MuiCurrencyFloatField from 'components/FormField/MuiCurrencyFloatField'
import { minWidth } from '@mui/system'
import MuiPhoneNumberField from 'components/FormField/MuiPhoneNumberField'
import MuiEmailField from 'components/FormField/MuiEmailField'
import MuiFloatField from 'components/FormField/MuiFloatField'
import NewTsp from 'layouts/authenticated/pages/tsp/NewTsp'
import { screenFileUpload } from 'layouts/authenticated/pages/documents/utils/uploadHelper'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function FinancialFrameworkCreator({
  collapseSections,
  toggleCollapse,
  valueOfInvestableAssets,
  adjustedNetTakeHomeDifferencePARCOGap,
  adjustedWorkingNetTakeHome,
  adjustedNetTakeHomeInRetirement,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
  atRetirementFERSSupplement,
  retirementAgeLessThanMRA,
  retirementLifestyleGoal = 1,
}) {
  const lifeStyleAdjustedGap =
    Math.abs(adjustedNetTakeHomeDifferencePARCOGap) -
    (1 - retirementLifestyleGoal) * adjustedWorkingNetTakeHome
  const [loading, setLoading] = useState(false)
  const [localForm, setLocalForm] = useState({
    pitchType:
      intakeState?.financialFramework?.pitchType || 'aggressive_income',
    incomeType:
      intakeState?.financialFramework?.incomeType ||
      (intakeState?.familyMaritalStatus === 'married' ? 'joint' : 'single'),
    objective1: intakeState?.financialFramework?.objective1 || '',
    objective2: intakeState?.financialFramework?.objective2 || '',
    carrier1: intakeState?.financialFramework?.carrier1 || '',
    carrier2: intakeState?.financialFramework?.carrier2 || '',
    product1: intakeState?.financialFramework?.product1 || '',
    product2: intakeState?.financialFramework?.product2 || '',
    trackAIncomeType:
      intakeState?.financialFramework?.trackAIncomeType || 'level',
    trackBIncomeType:
      intakeState?.financialFramework?.trackBIncomeType || 'level',
    trackCIncomeType:
      intakeState?.financialFramework?.trackCIncomeType || 'level',
    trackASolveFor: intakeState?.financialFramework?.trackASolveFor || 'income',
    trackBSolveFor: intakeState?.financialFramework?.trackBSolveFor || 'income',
    trackCSolveFor: intakeState?.financialFramework?.trackCSolveFor || 'income',
    estimatedMonthlyExpenses:
      intakeState?.financialFramework?.estimatedMonthlyExpenses || '',
    incomeTerm: intakeState?.financialFramework?.incomeTerm || 'monthly',
    annuityAmountA: intakeState?.financialFramework?.annuityAmountA || '',
    annuityAmountB: intakeState?.financialFramework?.annuityAmountB || '',
    annuityAmountC: intakeState?.financialFramework?.annuityAmountC || '',
    // swap these out
    trackASolveAmount: intakeState?.financialFramework?.trackASolveAmount || '',
    trackBSolveAmount: intakeState?.financialFramework?.trackBSolveAmount || '',
    trackCSolveAmount: intakeState?.financialFramework?.trackCSolveAmount || '',
    annuity1MonthlyFor100:
      intakeState?.financialFramework?.annuity1MonthlyFor100 || '',
    annuity2MonthlyFor100:
      intakeState?.financialFramework?.annuity2MonthlyFor100 || '',
    annuity1AmountA: intakeState?.financialFramework?.annuity1AmountA || '',
    annuity1AmountB: intakeState?.financialFramework?.annuity1AmountB || '',
    annuity1AmountC: intakeState?.financialFramework?.annuity1AmountC || '',
    annuity2AmountA: intakeState?.financialFramework?.annuity2AmountA || '',
    annuity2AmountB: intakeState?.financialFramework?.annuity2AmountB || '',
    annuity2AmountC: intakeState?.financialFramework?.annuity2AmountC || '',
    annuity1AnnualIncomeA:
      intakeState?.financialFramework?.annuity1AnnualIncomeA || '',
    annuity2AnnualIncomeA:
      intakeState?.financialFramework?.annuity2AnnualIncomeA || '',
    annuity1AnnualIncomeB:
      intakeState?.financialFramework?.annuity1AnnualIncomeB || '',
    annuity2AnnualIncomeB:
      intakeState?.financialFramework?.annuity2AnnualIncomeB || '',
    annuity1AnnualIncomeC:
      intakeState?.financialFramework?.annuity1AnnualIncomeC || '',
    annuity2AnnualIncomeC:
      intakeState?.financialFramework?.annuity2AnnualIncomeC || '',
    tspAllocationGraph:
      intakeState?.financialFramework?.tspAllocationGraph || '',
    tspMarketExposureGraph:
      intakeState?.financialFramework?.tspMarketExposureGraph || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const onBlur = () => {
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        financialFramework: localForm,
      })
    )()
  }
  const incomeDoublerRate = localForm.incomeType === 'joint' ? 1.5 : 2

  const leftoverFunds = Math.round(
    (valueOfInvestableAssets - ((lifeStyleAdjustedGap / 0.8) * 12) / 0.03) /
      1000
  )
  const original_withdrawl_bucket = Math.round(
    ((lifeStyleAdjustedGap / 0.8) * 12) / 0.03 / 1000
  )

  const customObjective1 = !selectData.retirementObjectives.find(
    ({ value }) => value === localForm.objective1
  )?.label

  const ss_or_supplement = retirementAgeLessThanMRA
    ? 'Pension'
    : atRetirementFERSSupplement === 0
    ? 'Pension + Net Social Security'
    : 'Pension + Net Supplement'

  const payload = {
    deckType: localForm.pitchType || 'aggressive_income',
    REP_NAME: `${
      selectData.retirementStrategist.find(
        (ac) =>
          ac.value === (intakeState?.retirementStrategist || 'john_mantia')
      ).firstName
    } ${
      selectData.retirementStrategist.find(
        (ac) =>
          ac.value === (intakeState?.retirementStrategist || 'john_mantia')
      ).lastName
    }`,
    tspBalance: intakeState?.tspBalance || 0,
    tspRothBalance: intakeState?.tspRothBalance || 0,
    tspAllocation: intakeState?.tspAllocation,
    SS_OR_SUPPLEMENT: ss_or_supplement,
    AGENCY:
      selectData.agency.find((c) => c.value === intakeState?.agency) || {},
    CLIENT_NAME: `${intakeState?.firstName} ${intakeState?.lastName}`,
    CLIENT_FIRST_NAME: `${intakeState?.firstName}`,
    DATE: format(new Date(), 'MM/dd/yyyy'),
    CLIENT_GOALS_1:
      selectData.retirementObjectives.find(
        ({ value }) => value === localForm.objective1
      )?.label || localForm.objective1,
    CLIENT_GOALS_2:
      selectData.retirementObjectives.find(
        ({ value }) => value === localForm.objective2
      )?.label || localForm.objective2,
    INCOME_GAP: dollarFormat.format(Math.abs(lifeStyleAdjustedGap || 0)),
    WORKING_INCOME: dollarFormat.format(adjustedWorkingNetTakeHome),
    RETIREMENT_INCOME: dollarFormat.format(adjustedNetTakeHomeInRetirement),
    WITHDRAWL_TERM:
      localForm.incomeTerm === 'monthly' ? '$500/MO' : '$6,000/YR',
    THREE_MONTHLY: dollarFormat.format(lifeStyleAdjustedGap),
    THREE_ANNUAL: dollarFormat.format(lifeStyleAdjustedGap * 12),
    THREE_BALANCE: dollarFormat.format((lifeStyleAdjustedGap * 12) / 0.03),
    THREE_MONTHLY_TAX: dollarFormat.format(lifeStyleAdjustedGap / 0.8),
    THREE_ANNUAL_TAX: dollarFormat.format((lifeStyleAdjustedGap / 0.8) * 12),
    THREE_BALANCE_TAX: dollarFormat.format(
      ((lifeStyleAdjustedGap / 0.8) * 12) / 0.03
    ),
    LEFTOVER_FUNDS:
      leftoverFunds < 0
        ? "We Don't Have Enough To Bridge the Gap!"
        : `Leaves Only ${dollarFormat.format(leftoverFunds)}k for ${
            localForm?.pitchType === 'aggressive_income'
              ? 'Aggressive Investing'
              : 'Other Goals'
          }`,
    LEFTOVER_FUNDS_BUCKET: `${dollarFormat.format(leftoverFunds)}k`,
    ORIGINAL_WITHDRAWL_BUCKET: `${dollarFormat.format(
      original_withdrawl_bucket
    )}k`,
    REMOVE_BUCKETS: leftoverFunds < 0 ? 'Remove' : 'Keep',
    CARRIER_1:
      selectData.annuityCarriers.find((c) => c.value === localForm.carrier1) ||
      {},
    PRODUCT_1: (() => {
      const p = selectData.annuityOptions.find(
        (c) => c.value === localForm.product1
      )
      const startIndex =
        20 +
        p?.valuePoints?.carrierRating?.length +
        p?.valuePoints?.vp2?.length +
        p?.valuePoints?.vp3?.indexOf(p.valuePoints?.annualFee)
      const carrierStart = 18 + ss_or_supplement.length
      return p
        ? {
            ...p,
            valuePoints: {
              ...p.valuePoints,
              vp3: p.valuePoints?.vp3?.replace(
                p.valuePoints?.annualFee,
                '{product_1_fee}'
              ),
              vp3Start: startIndex,
              vp3End: startIndex + p.valuePoints?.annualFee.length,
              carrierStart: carrierStart,
              carrierEnd: carrierStart + localForm.carrier1.length + 4,
            },
          }
        : {}
    })(),
    CARRIER_2:
      selectData.annuityCarriers.find((c) => c.value === localForm.carrier2) ||
      {},
    PRODUCT_2: (() => {
      const p = selectData.annuityOptions.find(
        (c) => c.value === localForm.product2
      )
      const startIndex =
        20 +
        p?.valuePoints?.carrierRating?.length +
        p?.valuePoints?.vp2?.length +
        p?.valuePoints?.vp3?.indexOf(p.valuePoints?.annualFee)
      return p
        ? {
            ...p,
            valuePoints: {
              ...p.valuePoints,
              vp3: p.valuePoints?.vp3?.replace(
                p.valuePoints?.annualFee,
                '{product_2_fee}'
              ),
              vp3Start: startIndex,
              vp3End: startIndex + p.valuePoints?.annualFee.length,
            },
          }
        : {}
    })(),
    ANNUITY_A: `${dollarFormat.format(
      Math.round(localForm.annuityAmountA / 1000)
    )}k`,
    ANNUITY_B: `${dollarFormat.format(
      Math.round(localForm.annuityAmountB / 1000)
    )}k`,
    ANNUITY_C: `${dollarFormat.format(
      Math.round(localForm.annuityAmountC / 1000)
    )}k`,
    AGGRESSIVE_BUCKET: `${dollarFormat.format(
      Math.round((valueOfInvestableAssets - localForm.annuityAmountA) / 1000)
    )}k`,
    AGGRESSIVE_BUCKET_LONG: `${dollarFormat.format(
      Math.round(valueOfInvestableAssets - localForm.annuityAmountA)
    )}`,
    AGGRESSIVE_BUCKET_B: `${dollarFormat.format(
      Math.round((valueOfInvestableAssets - localForm.annuityAmountB) / 1000)
    )}k`,
    AGGRESSIVE_BUCKET_B_LONG: `${dollarFormat.format(
      Math.round(valueOfInvestableAssets - localForm.annuityAmountB)
    )}`,
    AGGRESSIVE_BUCKET_C: `${dollarFormat.format(
      Math.round((valueOfInvestableAssets - localForm.annuityAmountC) / 1000)
    )}k`,
    AGGRESSIVE_BUCKET_C_LONG: `${dollarFormat.format(
      Math.round(valueOfInvestableAssets - localForm.annuityAmountC)
    )}`,
    ANNUITY_1_INCOME_A: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeA
        : localForm.annuity1AnnualIncomeA / 12
    ),
    ANNUITY_1_INCOME_A_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeA * incomeDoublerRate
        : (localForm.annuity1AnnualIncomeA / 12) * incomeDoublerRate
    ),
    ANNUITY_2_INCOME_A: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeA
        : localForm.annuity2AnnualIncomeA / 12
    ),
    ANNUITY_2_INCOME_A_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeA * incomeDoublerRate
        : (localForm.annuity2AnnualIncomeA / 12) * incomeDoublerRate
    ),
    ANNUITY_1_INCOME_B: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeB
        : localForm.annuity1AnnualIncomeB / 12
    ),
    ANNUITY_1_INCOME_B_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeB * incomeDoublerRate
        : (localForm.annuity1AnnualIncomeB / 12) * incomeDoublerRate
    ),
    ANNUITY_2_INCOME_B: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeB
        : localForm.annuity2AnnualIncomeB / 12
    ),
    ANNUITY_2_INCOME_B_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeB * incomeDoublerRate
        : (localForm.annuity2AnnualIncomeB / 12) * incomeDoublerRate
    ),
    ANNUITY_1_INCOME_C: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeC
        : localForm.annuity1AnnualIncomeC / 12
    ),
    ANNUITY_1_INCOME_C_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity1AnnualIncomeC * incomeDoublerRate
        : (localForm.annuity1AnnualIncomeC / 12) * incomeDoublerRate
    ),
    ANNUITY_2_INCOME_C: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeC
        : localForm.annuity2AnnualIncomeC / 12
    ),
    ANNUITY_2_INCOME_C_NURSING: dollarFormat.format(
      localForm.incomeTerm === 'monthly'
        ? localForm.annuity2AnnualIncomeC * incomeDoublerRate
        : (localForm.annuity2AnnualIncomeC / 12) * incomeDoublerRate
    ),
    RETIREMENT_INCOME_A: dollarFormat.format(
      adjustedNetTakeHomeInRetirement +
        (1 - (intakeState?.taxRate || 20) / 100) *
          (localForm.incomeTerm === 'monthly'
            ? localForm.annuity2AnnualIncomeA
            : localForm.annuity2AnnualIncomeA / 12)
    ),
    RETIREMENT_INCOME_A_DIFF: `${dollarFormat.format(
      (1 - (intakeState?.taxRate || 20) / 100) *
        (localForm.incomeTerm === 'monthly'
          ? localForm.annuity2AnnualIncomeA
          : localForm.annuity2AnnualIncomeA / 12) -
        lifeStyleAdjustedGap
    )}/MO`,
    RETIREMENT_INCOME_B: dollarFormat.format(
      adjustedNetTakeHomeInRetirement +
        (1 - (intakeState?.taxRate || 20) / 100) *
          (localForm.incomeTerm === 'monthly'
            ? localForm.annuity2AnnualIncomeB
            : localForm.annuity2AnnualIncomeB / 12)
    ),
    RETIREMENT_INCOME_B_DIFF: `${dollarFormat.format(
      (1 - (intakeState?.taxRate || 20) / 100) *
        (localForm.incomeTerm === 'monthly'
          ? localForm.annuity2AnnualIncomeB
          : localForm.annuity2AnnualIncomeB / 12) -
        lifeStyleAdjustedGap
    )}/MO`,
    RETIREMENT_INCOME_C: dollarFormat.format(
      adjustedNetTakeHomeInRetirement +
        (1 - (intakeState?.taxRate || 20) / 100) *
          (localForm.incomeTerm === 'monthly'
            ? localForm.annuity2AnnualIncomeC
            : localForm.annuity2AnnualIncomeC / 12)
    ),
    RETIREMENT_INCOME_C_DIFF: `${dollarFormat.format(
      (1 - (intakeState?.taxRate || 20) / 100) *
        (localForm.incomeTerm === 'monthly'
          ? localForm.annuity2AnnualIncomeC
          : localForm.annuity2AnnualIncomeC / 12) -
        lifeStyleAdjustedGap
    )}/MO`,
    TSP_ALLOCATION_GRAPH: localForm.tspAllocationGraph,
    TSP_PROTECTED_GRAPH: localForm.tspMarketExposureGraph,
  }
  // console.log(payload)

  const hiddenRef = useRef(null)
  const hiddenRef2 = useRef(null)

  const handleCaptureAndUpload = async () => {
    if (!hiddenRef.current || !hiddenRef2.current) return

    const captureCanvas = async (ref, label) => {
      const canvas = await html2canvas(ref, {
        backgroundColor: '#fff',
        scale: 2,
      })

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png')
      )

      if (!blob) throw new Error('Image generation failed.')

      const file = new File([blob], `tsp-${label}-${Date.now()}.png`, {
        type: 'image/png',
      })

      return await screenFileUpload(file)
    }

    try {
      const [allocationUrl, exposureUrl] = await Promise.all([
        captureCanvas(hiddenRef.current, 'allocation'),
        captureCanvas(hiddenRef2.current, 'exposure'),
      ])

      // Optional: update localForm state
      setLocalForm((prev) => ({
        ...prev,
        tspAllocationGraph: allocationUrl,
        tspMarketExposureGraph: exposureUrl,
      }))

      return {
        tspAllocationGraph: allocationUrl,
        tspMarketExposureGraph: exposureUrl,
      }
    } catch (err) {
      console.error('❌ Upload failed:', err)
      return {}
    }
  }

  const createFinancialFrameworkDeck = async () => {
    setLoading(true)
    try {
      const { tspAllocationGraph, tspMarketExposureGraph } =
        await handleCaptureAndUpload()

      const updatedPayload = {
        ...payload,
        TSP_ALLOCATION_GRAPH: tspAllocationGraph,
        TSP_PROTECTED_GRAPH: tspMarketExposureGraph,
      }

      // console.log(updatedPayload)

      await callZapier({
        user: intakeState,
        hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/uyykked/',
        payload: updatedPayload,
        notificationMessage:
          'You have successfully started the `Create Financial Framework` Automation',
        sendNotification: true,
      })
    } catch (error) {
      console.error('Failed to create pitch deck:', error)
    } finally {
      setLoading(false)
    }
  }

  const selectStyles = {
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': {
      color: '#ea0606',
    },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div>.MuiInputBase-input>.MuiListItemIcon-root': {
      minWidth: 'unset',
    },
    '&>div>.MuiInputBase-input>.MuiListItemIcon-root>img': {
      height: '20px',
      width: 'auto',
      marginRight: '8px',
    },
    '&>div>.MuiInputBase-input>.MuiListItemText-root>span': {
      fontSize: '14px',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
  }
  const carrierLogoStyles = {
    '&>.MuiListItemIcon-root': {
      minWidth: '100px',
      justifyContent: 'center',
    },
    '&>.MuiListItemIcon-root>img': {
      width: 'auto',
      height: 20,
      marginRight: '8px',
    },
    '&>.MuiListItemText-root': {},
    '&>.MuiListItemText-root>span': { fontSize: '14px' },
  }
  const filter = createFilterOptions()
  const comboStyles = {
    '&>div>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>div>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>div>label.MuiFormLabel-filled, &>div>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>div>div.MuiInputBase-root': {
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      borderRadius: '4px !important',
    },
  }
  const textStyles = {}

  // filteringLogic
  const isIncomeType = localForm.pitchType?.endsWith('income')
  const filteredCarriers = selectData.annuityCarriers.filter((carrier) =>
    isIncomeType ? carrier.hasIncomeProducts : carrier.hasAccumulationProducts
  )
  const getFilteredProducts = (carrierValue) => {
    return selectData.annuityOptions.filter(
      (option) =>
        option.provider === carrierValue &&
        (isIncomeType ? option.isIncome : option.isAccumulation)
    )
  }
  const filteredProducts1 = getFilteredProducts(localForm.carrier1)
  const filteredProducts2 = getFilteredProducts(localForm.carrier2)

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        {/* <Card
            sx={{
              overflow: 'visible',
              textAlign: 'left',
              boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid item xs={12}>
                <Typography variant="valueTitle">VALUE POINTS</Typography>
                <Collapse
                  in={collapseSections.financialframeworkcreator}
                  style={{ width: '100%' }}
                ></Collapse>
              </Grid>
            </Grid>
          </Card> */}
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="financialframeworkcreator"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('financialframeworkcreator')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Financial Framework Creator
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.financialframeworkcreator
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse
            in={collapseSections.financialframeworkcreator}
            style={{ width: '100%' }}
          >
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={12}>
                <Typography variant="body1Medium">
                  Drivers & Reference
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="working-income"
                  label="Working Income"
                  value={dollarFormat.format(adjustedWorkingNetTakeHome)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="investable-assets"
                  label="Investable Assets"
                  value={dollarFormat.format(valueOfInvestableAssets)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="retirement-income"
                  label="Income in Retirement"
                  value={dollarFormat.format(adjustedNetTakeHomeInRetirement)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="traditional-tsp-assets"
                  label="Traditional TSP Assets"
                  value={dollarFormat.format(intakeState?.tspBalance || 0)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="income-gap"
                  label="Income Gap - Net"
                  value={dollarFormat.format(lifeStyleAdjustedGap)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="years-till-retirement"
                  label="Est. Years Until Retirement"
                  value={(
                    differenceInMonths(
                      new Date(
                        intakeState?.dateOfPlannedRetirement || new Date()
                      ),
                      new Date()
                    ) / 12
                  ).toFixed(2)}
                  disabled
                  fullWidth
                  sx={selectStyles}
                ></TextField>
              </Grid>
              <Grid item xs={12} mt={2}>
                <Typography variant="body1Medium">Type & Objectives</Typography>
              </Grid>
              <Grid item xs={localForm.pitchType.endsWith('income') ? 6 : 12}>
                <TextField
                  id="pitch-type"
                  label="Pitch Type"
                  value={localForm.pitchType}
                  onChange={(e) => handleChange('pitchType')(e.target.value)}
                  onBlur={onBlur}
                  fullWidth
                  select
                  sx={selectStyles}
                >
                  <MenuItem value={'conservative_income'}>
                    Conservative Income
                  </MenuItem>
                  <MenuItem value={'aggressive_income'}>
                    Aggressive Income
                  </MenuItem>
                  <MenuItem value={'conservative_accumulation'}>
                    Conservative Accumulation
                  </MenuItem>
                  <MenuItem value={'aggressive_accumulation'}>
                    Aggressive Accumulation
                  </MenuItem>
                </TextField>
              </Grid>
              {localForm.pitchType.endsWith('income') && (
                <Grid item xs={6}>
                  <TextField
                    id="income-type"
                    label="Income Type"
                    value={localForm.incomeType}
                    onChange={(e) => handleChange('incomeType')(e.target.value)}
                    onBlur={onBlur}
                    fullWidth
                    select
                    sx={selectStyles}
                  >
                    <MenuItem value={'joint'}>Joint</MenuItem>
                    <MenuItem value={'single'}>Single</MenuItem>
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={localForm.objective1}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      handleChange('objective1')(newValue)
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      handleChange('objective1')(newValue.inputValue)
                    } else {
                      handleChange('objective1')(newValue?.label || '')
                    }
                  }}
                  onBlur={onBlur}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params)

                    const { inputValue } = params
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.label
                    )
                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        inputValue,
                        label: `Add "${inputValue}"`,
                      })
                    }

                    return filtered
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  id="autocomplete-objective1"
                  options={selectData.retirementObjectives}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === 'string') {
                      return option
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.inputValue
                    }
                    // Regular option
                    return option.label
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props
                    return (
                      <li key={key} {...optionProps}>
                        {option.label}
                      </li>
                    )
                  }}
                  fullWidth
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} label="Objective #1" />
                  )}
                  sx={comboStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={localForm.objective2}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      handleChange('objective2')(newValue)
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      handleChange('objective2')(newValue.inputValue)
                    } else {
                      handleChange('objective2')(newValue?.label || '')
                    }
                  }}
                  onBlur={onBlur}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params)

                    const { inputValue } = params
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.label
                    )
                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        inputValue,
                        label: `Add "${inputValue}"`,
                      })
                    }

                    return filtered
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  id="autocomplete-objective2"
                  options={selectData.retirementObjectives}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === 'string') {
                      return option
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.inputValue
                    }
                    // Regular option
                    return option.label
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props
                    return (
                      <li key={key} {...optionProps}>
                        {option.label}
                      </li>
                    )
                  }}
                  fullWidth
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} label="Objective #2" />
                  )}
                  sx={comboStyles}
                />
              </Grid>
              <Grid item xs={12} mt={2}>
                <Typography variant="body1Medium">
                  Product Recommendation #1
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="select-carrier1"
                  label="Carrier"
                  value={localForm.carrier1}
                  onChange={(e) => handleChange('carrier1')(e.target.value)}
                  onBlur={onBlur}
                  select
                  fullWidth
                  sx={selectStyles}
                >
                  {filteredCarriers.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={carrierLogoStyles}
                    >
                      <ListItemIcon>
                        <img src={option.logo} alt={option.label} />
                      </ListItemIcon>
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="select-product1"
                  label="Product"
                  value={localForm.product1}
                  onChange={(e) => handleChange('product1')(e.target.value)}
                  onBlur={onBlur}
                  select
                  fullWidth
                  sx={selectStyles}
                >
                  {filteredProducts1.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {localForm.pitchType.endsWith('income') && (
                <Grid item xs={12} sm={4}>
                  {renderMuiFormField({
                    label: 'Monthly Income for $100k',
                    shrink: true,
                    placeholder: '$1,000.00',
                    inputType: 'currencyFloat',
                    value: localForm.annuity1MonthlyFor100,
                    onChange: (e) => {
                      handleChange('annuity1MonthlyFor100')(e)
                      // handleChange('annuity2AnnualIncomeA')(e)
                    },
                    onBlur: onBlur,
                  })}
                </Grid>
              )}
              <Grid item xs={12} mt={2}>
                <Typography variant="body1Medium">
                  Product Recommendation #2
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="select-carrier2"
                  label="Carrier"
                  value={localForm.carrier2}
                  onChange={(e) => handleChange('carrier2')(e.target.value)}
                  onBlur={onBlur}
                  select
                  fullWidth
                  sx={selectStyles}
                >
                  {filteredCarriers.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={carrierLogoStyles}
                    >
                      <ListItemIcon>
                        <img src={option.logo} alt={option.label} />
                      </ListItemIcon>
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="select-product2"
                  label="Product"
                  value={localForm.product2}
                  onChange={(e) => handleChange('product2')(e.target.value)}
                  onBlur={onBlur}
                  select
                  fullWidth
                  sx={selectStyles}
                >
                  {filteredProducts2.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {localForm.pitchType.endsWith('income') && (
                <Grid item xs={12} sm={4}>
                  {renderMuiFormField({
                    label: 'Monthly Income for $100k',
                    shrink: true,
                    placeholder: '$1,000.00',
                    inputType: 'currencyFloat',
                    value: localForm.annuity2MonthlyFor100,
                    onChange: (e) => {
                      handleChange('annuity2MonthlyFor100')(e)
                      // handleChange('annuity2AnnualIncomeA')(e)
                    },
                    onBlur: onBlur,
                  })}
                </Grid>
              )}
              {/* <Grid item xs={12} mt={2}>
                <Typography variant="body1Medium">Optional Details</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiCurrencyFloatField
                  shrink
                  label="Estimated Monthly Expenses"
                  placeholder="$1,435.00"
                  value={localForm.estimatedMonthlyExpenses}
                  onChange={(e) => handleChange('estimatedMonthlyExpenses')(e)}
                  onBlur={onBlur}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="income-term"
                  label="Show Income Monthly or Annual"
                  value={localForm.incomeTerm}
                  onChange={(e) => handleChange('incomeTerm')(e.target.value)}
                  onBlur={onBlur}
                  select
                  fullWidth
                  sx={selectStyles}
                >
                  <MenuItem value={'monthly'}>Monthly</MenuItem>
                  <MenuItem value={'annual'}>Annual</MenuItem>
                </TextField>
              </Grid> */}
              {localForm.pitchType.endsWith('income') && (
                <>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="body1Medium">Track A</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackA-income-type"
                      label="Income Type"
                      value={localForm.trackAIncomeType}
                      onChange={(e) =>
                        handleChange('trackAIncomeType')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'level'}>Level</MenuItem>
                      <MenuItem value={'increasing'}>Increasing</MenuItem>
                      <MenuItem value={'decreasing'}>Decreasing</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackA-solve-for"
                      label="Solve For"
                      value={localForm.trackASolveFor}
                      onChange={(e) =>
                        handleChange('trackASolveFor')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'income'}>Income</MenuItem>
                      <MenuItem value={'premium'}>Premium</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label:
                        localForm.trackASolveFor === 'income'
                          ? 'Annuity Premium Amount'
                          : 'Desired Monthly Income Amount',
                      shrink: true,
                      placeholder:
                        localForm.trackASolveFor === 'income'
                          ? '$100,000.00'
                          : '$2,000.00',
                      inputType: 'currencyFloat',
                      value:
                        localForm.trackASolveFor === 'income'
                          ? localForm.annuityAmountA
                          : localForm.annuity1AnnualIncomeA,
                      onChange: (e) => {
                        if (localForm.trackASolveFor === 'income') {
                          handleChange('annuityAmountA')(e)
                          handleChange('annuity1AmountA')(e)
                          handleChange('annuity2AmountA')(e)
                          handleChange('annuity1AnnualIncomeA')(
                            (e / 100000) *
                              (localForm.annuity1MonthlyFor100 || 0)
                          )
                          handleChange('annuity2AnnualIncomeA')(
                            (e / 100000) *
                              (localForm.annuity2MonthlyFor100 || 0)
                          )
                        } else {
                          handleChange('annuity1AnnualIncomeA')(e)
                          handleChange('annuity2AnnualIncomeA')(e)
                          handleChange('annuity1AmountA')(
                            (e / (localForm.annuity1MonthlyFor100 || 0)) *
                              100000
                          )
                          handleChange('annuity2AmountA')(
                            (e / (localForm.annuity2MonthlyFor100 || 0)) *
                              100000
                          )
                        }
                      },
                      onBlur: onBlur,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product1"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product1
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AnnualIncomeA,
                      onChange: (e) => handleChange('annuity1AnnualIncomeA')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AmountA,
                      onChange: (e) => {
                        handleChange('annuity1AmountA')(e)
                        handleChange('annuityAmountA')(e)
                      },
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product2"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product2
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AnnualIncomeA,
                      onChange: (e) => handleChange('annuity2AnnualIncomeA')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AmountA,
                      onChange: (e) => handleChange('annuity2AmountA')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="body1Medium">Track B</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackB-income-type"
                      label="Income Type"
                      value={localForm.trackBIncomeType}
                      onChange={(e) =>
                        handleChange('trackBIncomeType')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'level'}>Level</MenuItem>
                      <MenuItem value={'increasing'}>Increasing</MenuItem>
                      <MenuItem value={'decreasing'}>Decreasing</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackB-solve-for"
                      label="Solve For"
                      value={localForm.trackBSolveFor}
                      onChange={(e) =>
                        handleChange('trackBSolveFor')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'income'}>Income</MenuItem>
                      <MenuItem value={'premium'}>Premium</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label:
                        localForm.trackBSolveFor === 'income'
                          ? 'Annuity Premium Amount'
                          : 'Desired Monthly Income Amount',
                      shrink: true,
                      placeholder:
                        localForm.trackBSolveFor === 'income'
                          ? '$100,000.00'
                          : '$2,000.00',
                      inputType: 'currencyFloat',
                      value:
                        localForm.trackBSolveFor === 'income'
                          ? localForm.annuityAmountB
                          : localForm.annuity1AnnualIncomeB,
                      onChange: (e) => {
                        if (localForm.trackBSolveFor === 'income') {
                          handleChange('annuityAmountB')(e)
                          handleChange('annuity1AmountB')(e)
                          handleChange('annuity2AmountB')(e)
                          handleChange('annuity1AnnualIncomeB')(
                            (e / 100000) *
                              (localForm.annuity1MonthlyFor100 || 0)
                          )
                          handleChange('annuity2AnnualIncomeB')(
                            (e / 100000) *
                              (localForm.annuity2MonthlyFor100 || 0)
                          )
                        } else {
                          handleChange('annuity1AnnualIncomeB')(e)
                          handleChange('annuity2AnnualIncomeB')(e)
                          handleChange('annuity1AmountB')(
                            (e / (localForm.annuity1MonthlyFor100 || 0)) *
                              100000
                          )
                          handleChange('annuity2AmountB')(
                            (e / (localForm.annuity2MonthlyFor100 || 0)) *
                              100000
                          )
                        }
                      },
                      onBlur: onBlur,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product1"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product1
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AnnualIncomeB,
                      onChange: (e) => handleChange('annuity1AnnualIncomeB')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AmountB,
                      onChange: (e) => {
                        handleChange('annuity1AmountB')(e)
                        handleChange('annuityAmountB')(e)
                      },
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product2"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product2
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AnnualIncomeB,
                      onChange: (e) => handleChange('annuity2AnnualIncomeB')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AmountB,
                      onChange: (e) => handleChange('annuity2AmountB')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="body1Medium">Track C</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackB-income-type"
                      label="Income Type"
                      value={localForm.trackCIncomeType}
                      onChange={(e) =>
                        handleChange('trackCIncomeType')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'level'}>Level</MenuItem>
                      <MenuItem value={'increasing'}>Increasing</MenuItem>
                      <MenuItem value={'decreasing'}>Decreasing</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="trackC-solve-for"
                      label="Solve For"
                      value={localForm.trackCSolveFor}
                      onChange={(e) =>
                        handleChange('trackCSolveFor')(e.target.value)
                      }
                      onBlur={onBlur}
                      fullWidth
                      select
                      sx={selectStyles}
                    >
                      <MenuItem value={'income'}>Income</MenuItem>
                      <MenuItem value={'premium'}>Premium</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label:
                        localForm.trackCSolveFor === 'income'
                          ? 'Annuity Premium Amount'
                          : 'Desired Monthly Income Amount',
                      shrink: true,
                      placeholder:
                        localForm.trackCSolveFor === 'income'
                          ? '$100,000.00'
                          : '$2,000.00',
                      inputType: 'currencyFloat',
                      value:
                        localForm.trackCSolveFor === 'income'
                          ? localForm.annuityAmountC
                          : localForm.annuity1AnnualIncomeC,
                      onChange: (e) => {
                        if (localForm.trackCSolveFor === 'income') {
                          handleChange('annuityAmountC')(e)
                          handleChange('annuity1AmountC')(e)
                          handleChange('annuity2AmountC')(e)
                          handleChange('annuity1AnnualIncomeC')(
                            (e / 100000) *
                              (localForm.annuity1MonthlyFor100 || 0)
                          )
                          handleChange('annuity2AnnualIncomeC')(
                            (e / 100000) *
                              (localForm.annuity2MonthlyFor100 || 0)
                          )
                        } else {
                          handleChange('annuity1AnnualIncomeC')(e)
                          handleChange('annuity2AnnualIncomeC')(e)
                          handleChange('annuity1AmountC')(
                            (e / (localForm.annuity1MonthlyFor100 || 0)) *
                              100000
                          )
                          handleChange('annuity2AmountC')(
                            (e / (localForm.annuity2MonthlyFor100 || 0)) *
                              100000
                          )
                        }
                      },
                      onBlur: onBlur,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product1"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product1
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AnnualIncomeC,
                      onChange: (e) => handleChange('annuity1AnnualIncomeC')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier1
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier1
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier1 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity1AmountC,
                      onChange: (e) => {
                        handleChange('annuity1AmountC')(e)
                        handleChange('annuityAmountC')(e)
                      },
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField
                      id="select-product2"
                      label={
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - Product'}
                          </span>
                        </>
                      }
                      value={
                        selectData.annuityOptions.find(
                          ({ value }) => value === localForm.product2
                        )?.label || ''
                      }
                      disabled
                      fullWidth
                      sx={selectStyles}
                    ></TextField>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === '' ? '' : ' - '}
                          </span>
                          <span>
                            {localForm.incomeTerm === 'monthly'
                              ? 'Monthly Income'
                              : 'Annual Income'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$2,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AnnualIncomeC,
                      onChange: (e) => handleChange('annuity2AnnualIncomeC')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    {renderMuiFormField({
                      label: (
                        <>
                          <span
                            className="carrier-label"
                            style={{
                              color:
                                selectData.annuityCarriers.find(
                                  ({ value }) => value === localForm.carrier2
                                )?.textColor || 'inherit',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                            }}
                          >
                            {selectData.annuityCarriers.find(
                              ({ value }) => value === localForm.carrier2
                            )?.label || ''}
                          </span>
                          <span className="carrier-label">
                            {localForm.carrier2 === ''
                              ? ''
                              : ' - Annuity Amount'}
                          </span>
                        </>
                      ),
                      shrink: true,
                      placeholder: '$100,000.00',
                      inputType: 'currencyFloat',
                      value: localForm.annuity2AmountC,
                      onChange: (e) => handleChange('annuity2AmountC')(e),
                      onBlur: onBlur,
                      disabled: true,
                    })}
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                {/* <Button variant="contained" onClick={handleCaptureAndUpload}>
                  Capture TSP Image
                </Button> */}

                {/* Hidden graph render */}
                <Box
                  ref={hiddenRef}
                  sx={{
                    position: 'absolute',
                    top: -9999,
                    left: -9999,
                    width: 800,
                    height: 'auto',
                    zIndex: 1,
                    backgroundColor: '#fff',
                    '&>div>div>svg': {
                      marginLeft: '20px',
                    },
                    '&>div>svg': {
                      overflow: 'visible',
                    },
                    '&>div': {
                      padding: '8px',
                    },
                  }}
                >
                  {/* <Typography variant="h3">TSP Holdings</Typography> */}
                  <NewTsp isDisplayOnly hideLabels={false} />
                </Box>

                <Box
                  ref={hiddenRef2}
                  sx={{
                    position: 'absolute',
                    top: -9999,
                    left: -9999,
                    width: 900,
                    height: 'auto',
                    zIndex: 1,
                    backgroundColor: '#fff',
                    '&>div>div>svg': {
                      marginLeft: '20px',
                    },
                    '&>div>svg': {
                      overflow: 'visible',
                    },
                    '&>div': {
                      padding: '8px',
                    },
                  }}
                >
                  {/* <Typography variant="h3">TSP Holdings</Typography> */}
                  <NewTsp isDisplayOnly showExposed hideLabels={false}></NewTsp>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="parcoRounded"
                  onClick={createFinancialFrameworkDeck}
                  endIcon={
                    loading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {loading ? 'Creating...' : 'Create Pitch Deck'}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default FinancialFrameworkCreator
